import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { RateBar, SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';
import { RecentTags } from '../components/RecentTags';
import { initial } from '../data/meta';

function PlayersList() {
  usePageMeta({ title: 'Players', description: 'Search tracked Brawl Stars players and see their trophies, brawlers and recent battles.', noindex: true });
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null); // a previous failure must not stick to the next search
    try {
      const data = await api.getPlayers(search || undefined, 100);
      setPlayers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Leaderboard</div>
            <h1>Players</h1>
            <div className="sub">Tracked players ranked by trophies. Open a profile for recent battles and a climb plan.</div>
          </div>
        </div>

        <div className="filters">
          <input
            className="search-input"
            placeholder="🔎 Search by name or tag…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <RecentTags game="brawl" label="Recently viewed" onPick={(t) => navigate(`/players/${encodeURIComponent(`#${t}`)}`)} />

        {error ? (
          <ErrorMessage message={error} onRetry={load} />
        ) : loading ? (
          <SkeletonRows rows={10} />
        ) : (
          <div className="tablecard">
            <div className="table-scroll">
              <table className="meta">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Player</th>
                    <th>Trophies</th>
                    <th className="col-pick">Highest</th>
                    <th>Win %</th>
                    <th className="col-map">Battles</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p, i) => (
                    <tr key={p.tag} onClick={() => navigate(`/players/${encodeURIComponent(p.tag)}`)}>
                      <td className="rank">{i + 1}</td>
                      <td>
                        <div className="pcell">
                          <span className="pav">{initial(p.name)}</span>
                          <div>
                            <div className="pn">{p.name}</div>
                            <div className="pt">{p.tag}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="num-cell">{p.trophies?.toLocaleString() ?? '—'}</span></td>
                      <td className="col-pick"><span className="num-cell">{p.highest_trophies?.toLocaleString() ?? '—'}</span></td>
                      <td><RateBar value={p.win_rate} max={100} lime /></td>
                      <td className="col-map"><span className="num-cell">{(p.total_battles ?? 0).toLocaleString()}</span></td>
                    </tr>
                  ))}
                  {players.length === 0 && (
                    <tr><td colSpan={6} className="empty-state">No players found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayersList;
